// frontend/src/Helpers/tts.js
// Polls /status/<server_task_id> to expose word + index for UI highlighting.
// No GUID concepts are surfaced to the UI/redux; highlighting is driven by `index`.

import {KAYTEK_LOCAL_SERVICE_BASE_URL} from './localServiceUrl'

export function startTTSEngine({
                                 idleFlushMs = 220,
                                 maxChunkChars = 140,
                                 url = `${KAYTEK_LOCAL_SERVICE_BASE_URL}/speak`,
                                 statusUrlBase = `${KAYTEK_LOCAL_SERVICE_BASE_URL}/status`,
                                 onRobotSpeakingChange = null,
                                 onRobotWordChange = null,
                               } = {}) {
  let buffer = "";
  let idleTimer = null;
  const spokenSentenceKeys = new Set();

  // speaking ref-count (supports overlapping tasks)
  let speakingRefCount = 0;

  // Monotonic sequencing for emitted word events (engine-local)
  let emitSeq = 0;

  const setSpeaking = (v) => {
    if (onRobotSpeakingChange) {
      try {
        onRobotSpeakingChange(!!v);
      } catch (e) {
        console.warn("onRobotSpeakingChange callback failed", e);
      }
    }
  };

  const incSpeaking = () => {
    speakingRefCount += 1;
    if (speakingRefCount === 1) setSpeaking(true);
  };

  const decSpeaking = () => {
    speakingRefCount = Math.max(0, speakingRefCount - 1);
    if (speakingRefCount === 0) setSpeaking(false);
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const pollUntilFinished = async (serverTaskId) => {
    let sawPlaying = false;
    let lastKey = null;

    try {
      for (;;) {
        let st = null;
        try {
          const resp = await fetch(`${statusUrlBase}/${encodeURIComponent(serverTaskId)}`, {
            method: "GET",
            cache: "no-store",
          });
          if (!resp.ok) {
            await sleep(150);
            continue;
          }
          st = await resp.json();

          // word highlight support
          if (onRobotWordChange && st && st.state === "playing") {
            const idx = typeof st.current_word_index === "number" ? st.current_word_index : null;
            const word = st.current_word ?? null;

            // De-dupe within the polling loop (same status repeated)
            const key = `${idx}:${word}`;
            if (key !== lastKey) {
              lastKey = key;
              try {
                onRobotWordChange({
                  word,
                });
              } catch (e) {
                console.warn("onRobotWordChange callback failed", e);
              }
            }
          }
        } catch (e) {
          await sleep(150);
          continue;
        }

        const state = st && st.state;

        if (state === "playing" && !sawPlaying) {
          sawPlaying = true;
          incSpeaking();
        }

        if (state === "done" || state === "error") {
          break;
        }

        await sleep(120);
      }
    } finally {
      if (sawPlaying) {
        await sleep(200);
        decSpeaking();
      }
    }
  };

  const resetIdle = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      flush();
    }, idleFlushMs);
  };

  const normalizeSentenceKey = (sentence) => {
    return (sentence || "").trim().replace(/\s+/g, " ");
  };

  const postSentence = async (sentence) => {
    const text = (sentence || "").trim();
    if (!text) return;

    const sentenceKey = normalizeSentenceKey(text);
    if (spokenSentenceKeys.has(sentenceKey)) {
      console.debug("gpt0522", "skip duplicate tts sentence", sentenceKey);
      return;
    }
    spokenSentenceKeys.add(sentenceKey);

    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({text, lang: "auto"}),
        keepalive: true,
      });

      // Server returns JSON {"job_id": "...", ...} - we treat it as an opaque server task id.
      try {
        const data = await resp.json();
        const serverTaskId = data && data.job_id;
        if (serverTaskId) {
          pollUntilFinished(serverTaskId); // fire-and-forget
        }
      } catch (e) {
        console.warn("TTS response JSON parse failed", e);
      }
    } catch (e) {
      console.warn("TTS POST failed", e);
    }
  };

  // Split buffer into [completeSentences..., remainder]
  const extractCompleteSentences = (text) => {
    const parts = [];
    let start = 0;

    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === "." || ch === "!" || ch === "?" || ch === "\n") {
        const sentence = text.slice(start, i + 1);
        parts.push(sentence);
        start = i + 1;
      }
    }
    const remainder = text.slice(start);
    return {sentences: parts, remainder};
  };

  const flush = async () => {
    if (idleTimer) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }

    const toSend = buffer;

    if (!toSend.trim()) return;

    const {sentences, remainder} = extractCompleteSentences(toSend);
    for (const s of sentences) await postSentence(s);

    // gpt0522: do not speak incomplete text on idle; keep it until sentence punctuation arrives
    buffer = remainder;
  };

  const pushToken = async (token) => {
    const t = token ?? "";
    if (!t) return;

    buffer += t;

    if (buffer.length >= maxChunkChars) {
      const {sentences, remainder} = extractCompleteSentences(buffer);

      if (sentences.length) {
        for (const s of sentences) await postSentence(s);
        buffer = remainder;
      } else {
        // gpt0522: wait instead of speaking a partial sentence; avoids repeats when slow text later completes it
        console.debug("gpt0522", "tts buffer reached maxChunkChars without sentence boundary; waiting", buffer.length);
      }
    } else {
      const {sentences, remainder} = extractCompleteSentences(buffer);
      if (sentences.length) {
        for (const s of sentences) await postSentence(s);
        buffer = remainder;
      }
    }

    resetIdle();
  };

  return {
    pushToken,
    flush,
  };
}
